import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { 
  Plus, 
  Edit, 
  Trash2, 
  Package,
  Upload,
  Download,
  FileSpreadsheet,
  Loader2,
  CheckCircle,
  AlertCircle,
  Link,
  RefreshCw,
  Search,
  GripVertical,
  ArrowRight,
  TriangleAlert,
  User
} from "lucide-react";

import LedgerDialog from "@/components/LedgerDialog";

// SKU Types
const SKU_TYPES = [
  { id: "TIN", label: "TIN", color: "bg-blue-100 text-blue-800 border-blue-300" },
  { id: "BUCKET", label: "BUCKET", color: "bg-green-100 text-green-800 border-green-300" },
  { id: "TIN_REJ", label: "TIN REJ", color: "bg-red-100 text-red-800 border-red-300" },
  { id: "VACUUM_10", label: "VACUUM 10", color: "bg-purple-100 text-purple-800 border-purple-300" },
  { id: "VACUUM_20", label: "VACUUM 20", color: "bg-orange-100 text-orange-800 border-orange-300" },
];

const GRADES = ["W180", "W210", "W240", "W320", "W450", "SW", "LP", "SP", "BB", "SB", "JH"];

export default function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSku, setSelectedSku] = useState("TIN");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);
  
  // Search state
  const [searchGrade, setSearchGrade] = useState("");
  const [searchSku, setSearchSku] = useState("TIN");
  const [activeProductTab, setActiveProductTab] = useState("effective");
  const [orderedGrades, setOrderedGrades] = useState([]);
  const [effectiveStock, setEffectiveStock] = useState([]);
  const [tabLoading, setTabLoading] = useState(false);
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  
  // Ordered grade detail popup
  const [orderedDetailOpen, setOrderedDetailOpen] = useState(false);
  const [orderedDetailGrade, setOrderedDetailGrade] = useState(null);
  const [orderedDetailData, setOrderedDetailData] = useState([]);
  const [orderedDetailLoading, setOrderedDetailLoading] = useState(false);

  // Dispatchable orders tab
  const [dispatchableOrders, setDispatchableOrders] = useState([]);
  const [dispatchableLoading, setDispatchableLoading] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [savingPriority, setSavingPriority] = useState(false);
  // Grade sort order
  const [gradeOrder, setGradeOrder] = useState({});
  const [gradeDragIdx, setGradeDragIdx] = useState(null);
  const isAdmin = (() => { try { return ['owner','admin'].includes(JSON.parse(localStorage.getItem('user') || '{}').role); } catch { return false; } })();

  // Ledger dialog from dispatchable tab
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [ledgerPartyName, setLedgerPartyName] = useState('');

  // Partial dispatch dialog
  const [partialDialogOpen, setPartialDialogOpen] = useState(false);
  const [partialOrder, setPartialOrder] = useState(null);
  const [partialItems, setPartialItems] = useState([]);
  const [sendingPartial, setSendingPartial] = useState(false);
  
  // Google Sheet sync
  const [sheetDialogOpen, setSheetDialogOpen] = useState(false);
  const [sheetSyncing, setSheetSyncing] = useState(false);
  const [sheetSyncStatus, setSheetSyncStatus] = useState(null);
  
  // Low stock state
  const [lowStockInfo, setLowStockInfo] = useState(null);
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  
  // Stock thresholds (editable)
  const [lowStockThreshold, setLowStockThreshold] = useState(10);
  const [highStockThreshold, setHighStockThreshold] = useState(100);
  const [showHighStockOnly, setShowHighStockOnly] = useState(false);
  const [savingThresholds, setSavingThresholds] = useState(false);

  const [formData, setFormData] = useState({
    sku: "TIN",
    grade: "",
    kgs_pack: "",
    qty: "",
    base_price: "",
    min_price: "",
    max_price: ""
  });

  useEffect(() => {
    fetchProducts();
    fetchSheetSyncStatus();
    fetchLowStock();
    fetchStockThresholds();
    fetchOrderedGrades();
    fetchEffectiveStock();
  }, []);

  // Fetch grade order when SKU changes
  useEffect(() => {
    axios.get(`${API}/products/grade-order?sku=${selectedSku}`).then(r => {
      setGradeOrder(prev => ({ ...prev, [selectedSku]: r.data?.order || [] }));
    }).catch(() => {});
  }, [selectedSku]);

  // Sort array by saved grade order
  const sortByGradeOrder = (arr, sku) => {
    const order = gradeOrder[sku] || [];
    if (!order.length) return arr;
    const orderMap = {};
    order.forEach((g, i) => { orderMap[g] = i; });
    return [...arr].sort((a, b) => {
      const aIdx = orderMap[a.grade] ?? 9999;
      const bIdx = orderMap[b.grade] ?? 9999;
      return aIdx - bIdx;
    });
  };

  const handleGradeDragEnd = async (arr, dropIdx, sku, setFn) => {
    if (gradeDragIdx === null || gradeDragIdx === dropIdx) { setGradeDragIdx(null); return; }
    const reordered = [...arr];
    const [moved] = reordered.splice(gradeDragIdx, 1);
    reordered.splice(dropIdx, 0, moved);
    setGradeDragIdx(null);
    // Save new order
    const newOrder = reordered.map(r => r.grade);
    setGradeOrder(prev => ({ ...prev, [sku]: newOrder }));
    if (setFn) setFn(reordered);
    try {
      await axios.put(`${API}/products/grade-order?token=${localStorage.getItem('token')}`, { sku, order: newOrder });
    } catch { /* */ }
  };



  const fetchOrderedGrades = async () => {
    try {
      const res = await axios.get(`${API}/products/ordered-grades`);
      setOrderedGrades(res.data || []);
    } catch (e) { }
  };

  const fetchEffectiveStock = async () => {
    try {
      const res = await axios.get(`${API}/products/effective-stock`);
      setEffectiveStock(res.data || []);
    } catch (e) { }
  };

  const fetchOrderedGradeDetails = async (grade, sku) => {
    setOrderedDetailLoading(true);
    setOrderedDetailGrade({ grade, sku });
    setOrderedDetailOpen(true);
    try {
      const res = await axios.get(`${API}/products/ordered-grade-details?grade=${encodeURIComponent(grade)}&sku=${encodeURIComponent(sku)}`);
      setOrderedDetailData(res.data || []);
    } catch (e) {
      toast.error("Failed to load order details");
      setOrderedDetailData([]);
    } finally {
      setOrderedDetailLoading(false);
    }
  };

  const fetchDispatchableOrders = async () => {
    setDispatchableLoading(true);
    try {
      const res = await axios.get(`${API}/products/dispatchable-orders`);
      setDispatchableOrders(res.data || []);
    } catch (e) {
      toast.error("Failed to load dispatchable orders");
    } finally {
      setDispatchableLoading(false);
    }
  };

  const handleDragStart = (idx) => {
    setDragIdx(idx);
  };
  const handleDragOver = (e, idx) => {
    e.preventDefault();
  };
  const handleDrop = async (e, dropIdx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === dropIdx) { setDragIdx(null); return; }
    const newList = [...dispatchableOrders];
    const [moved] = newList.splice(dragIdx, 1);
    newList.splice(dropIdx, 0, moved);
    setDispatchableOrders(newList);
    setDragIdx(null);
    // Save priority to DB with username
    setSavingPriority(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await axios.put(`${API}/orders/dispatch-priority`, { order_ids: newList.map(o => o.id), username: user.username || 'unknown' });
      await fetchDispatchableOrders();
    } catch (e) {
      toast.error("Failed to save priority");
    } finally {
      setSavingPriority(false);
    }
  };

  const sendToSort = async (orderId, orderNumber) => {
    try {
      await axios.put(`${API}/orders/${orderId}`, { status: 'to_sort' });
      toast.success(`${orderNumber} sent to warehouse (To Sort)`);
      fetchDispatchableOrders();
    } catch (e) {
      toast.error("Failed to send to sort");
    }
  };

  const openPartialDispatch = (order) => {
    setPartialOrder(order);
    // Initialize with all items, qty editable
    setPartialItems((order.items || []).map(item => ({
      grade: item.grade || '',
      sku: item.sku || item.packing || 'TIN',
      qty: item.qty || 0,
      rate: item.rate || 0,
      dispatchQty: 0, // user fills how many to dispatch
      maxQty: item.qty || 0
    })));
    setPartialDialogOpen(true);
  };

  const submitPartialToSort = async () => {
    if (!partialOrder) return;
    const selected = partialItems.filter(i => i.dispatchQty > 0);
    if (selected.length === 0) { toast.error("Select at least one grade to dispatch"); return; }
    
    setSendingPartial(true);
    try {
      const dispatchItems = selected.map(i => ({ grade: i.grade, sku: i.sku, qty: i.dispatchQty, rate: i.rate }));
      
      // Use the new partial-sort endpoint that creates a split order
      const res = await axios.post(`${API}/orders/${partialOrder.id}/partial-sort`, {
        dispatch_items: dispatchItems
      });
      
      toast.success(res.data?.message || `${partialOrder.order_number} partial sort done`);
      setPartialDialogOpen(false);
      setPartialOrder(null);
      fetchDispatchableOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send partial dispatch");
    } finally {
      setSendingPartial(false);
    }
  };



  const fetchStockThresholds = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setLowStockThreshold(response.data.low_stock_threshold || 10);
      setHighStockThreshold(response.data.high_stock_threshold || 100);
    } catch (error) {
    }
  };

  const saveStockThresholds = async () => {
    setSavingThresholds(true);
    try {
      await axios.put(`${API}/settings`, {
        low_stock_threshold: lowStockThreshold,
        high_stock_threshold: highStockThreshold
      });
      toast.success("Stock thresholds saved");
      fetchLowStock();
    } catch (error) {
      toast.error("Failed to save thresholds");
    } finally {
      setSavingThresholds(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      toast.error("Failed to fetch products");
    } finally {
      setLoading(false);
    }
  };

  const fetchLowStock = async () => {
    try {
      const response = await axios.get(`${API}/products/low-stock`);
      setLowStockInfo(response.data);
    } catch (error) {
    }
  };

  const fetchSheetSyncStatus = async () => {
    try {
      const response = await axios.get(`${API}/products/stock-sync-status`);
      setSheetSyncStatus(response.data);
    } catch (error) {
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      kgs_pack: parseFloat(formData.kgs_pack),
      qty: parseFloat(formData.qty),
      base_price: parseFloat(formData.base_price),
      min_price: parseFloat(formData.min_price),
      max_price: parseFloat(formData.max_price)
    };
    
    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, payload);
        toast.success("Product updated successfully");
      } else {
        await axios.post(`${API}/products`, payload);
        toast.success("Product added successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save product");
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      sku: product.sku,
      grade: product.grade,
      kgs_pack: product.kgs_pack.toString(),
      qty: product.qty.toString(),
      base_price: product.base_price.toString(),
      min_price: product.min_price.toString(),
      max_price: product.max_price.toString()
    });
    setDialogOpen(true);
  };

  const handleDelete = (product) => {
    setProductToDelete(product);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!productToDelete) return;
    try {
      await axios.delete(`${API}/products/${productToDelete.id}`);
      toast.success("Product deleted");
      setDeleteDialogOpen(false);
      setProductToDelete(null);
      fetchProducts();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData({
      sku: selectedSku,
      grade: "",
      kgs_pack: "",
      qty: "",
      base_price: "",
      min_price: "",
      max_price: ""
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    setUploadResult(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/products/upload-excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadResult(response.data);
      toast.success(`Created ${response.data.created}, Updated ${response.data.updated} products`);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload Excel");
      setUploadResult({ error: error.response?.data?.detail || "Upload failed" });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/products/download-template`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'products_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Template downloaded");
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  const handleSyncFromSheet = async () => {
    setSheetSyncing(true);
    try {
      const response = await axios.post(`${API}/products/sync-stock`);
      toast.success("Stock synced from Google Sheet");
      fetchProducts();
      fetchSheetSyncStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync from Google Sheet");
    } finally {
      setSheetSyncing(false);
    }
  };

  // Filter products by selected SKU - keep original order from Excel (no sorting)
  const filteredProducts = products.filter(product => {
    if (product.sku !== selectedSku) return false;
    if (showLowStockOnly && lowStockInfo) {
      const isLow = lowStockInfo.products.some(lp => lp.id === product.id);
      return isLow;
    }
    if (showHighStockOnly) {
      return product.qty > highStockThreshold;
    }
    return true;
  });
  
  // Check if a product is low stock
  const isLowStock = (productId) => {
    return lowStockInfo?.products?.some(lp => lp.id === productId);
  };
  
  // Check if a product is high stock
  const isHighStock = (product) => {
    return product.qty > highStockThreshold;
  };
  
  // Count high stock products for current SKU
  const highStockCount = products.filter(p => p.sku === selectedSku && p.qty > highStockThreshold).length;

  // Get unique grades for dropdown
  const uniqueGrades = [...new Set(products.map(p => p.grade))].sort();

  // Search result - find product by grade and SKU
  const searchResult = searchGrade 
    ? products.find(p => 
        p.sku === searchSku && 
        p.grade === searchGrade
      )
    : null;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  const getSkuColor = (skuId) => {
    const sku = SKU_TYPES.find(s => s.id === skuId);
    return sku?.color || "bg-gray-100 text-gray-800";
  };

  // Get TOTAL QTY by SKU (sum of all quantities)
  const getSkuTotalQty = (skuId) => {
    return products
      .filter(p => p.sku === skuId)
      .reduce((sum, p) => sum + (p.qty || 0), 0);
  };

  return (
    <div className="p-6" data-testid="products-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
            Products
          </h1>
          <p className="text-sm text-muted-foreground">
            Manage inventory by SKU
          </p>
        </div>
        <div className="flex gap-2">
          {/* Stock Thresholds */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5">
                <AlertCircle className="w-3.5 h-3.5" />
                Thresholds
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64" align="end">
              <div className="space-y-3">
                <div className="text-sm font-semibold">Stock Thresholds</div>
                <div>
                  <Label className="text-xs">Low Stock (≤)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={lowStockThreshold}
                    onChange={(e) => setLowStockThreshold(parseInt(e.target.value) || 0)}
                    className="h-8 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">High Stock (&gt;)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={highStockThreshold}
                    onChange={(e) => setHighStockThreshold(parseInt(e.target.value) || 0)}
                    className="h-8 mt-1"
                  />
                </div>
                <Button 
                  size="sm" 
                  className="w-full" 
                  onClick={saveStockThresholds}
                  disabled={savingThresholds}
                >
                  {savingThresholds ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Saving...</> : 'Save Thresholds'}
                </Button>
              </div>
            </PopoverContent>
          </Popover>

          {/* Google Sheet Sync */}
          <Dialog open={sheetDialogOpen} onOpenChange={setSheetDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5">
                <Link className="w-3.5 h-3.5" />
                Google Sheet
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Sync from Google Sheet</DialogTitle>
                <DialogDescription>
                  Sync product data from your Google Sheet
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className={`p-3 rounded-lg border ${sheetSyncStatus?.has_google_credentials ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                  {sheetSyncStatus?.has_google_credentials ? (
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle className="w-4 h-4" />
                      <span>Google credentials configured</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-amber-700">
                      <AlertCircle className="w-4 h-4" />
                      <span>Configure Google credentials in Settings → Sheets</span>
                    </div>
                  )}
                </div>
                {sheetSyncStatus?.last_stock_sync && (
                  <p className="text-sm text-muted-foreground">
                    Last sync: {new Date(sheetSyncStatus.last_stock_sync).toLocaleString()}
                  </p>
                )}
                <Button 
                  onClick={handleSyncFromSheet}
                  disabled={sheetSyncing || !sheetSyncStatus?.has_google_credentials}
                  className="w-full gap-2"
                >
                  {sheetSyncing ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Syncing...</>
                  ) : (
                    <><RefreshCw className="w-4 h-4" /> Sync Now</>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Upload Excel */}
          <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5">
                <Upload className="w-3.5 h-3.5" />
                Upload
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-primary" />
                  Upload Products
                </DialogTitle>
                <DialogDescription>
                  Upload Excel with columns: SKU, Grade, KGs Pack, Qty, Base Price, Min Price, Max Price
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                  <FileSpreadsheet className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".xlsx,.xls"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <Button 
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="gap-2"
                  >
                    {uploading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</>
                    ) : (
                      <><Upload className="w-4 h-4" /> Select File</>
                    )}
                  </Button>
                </div>
                
                <Button variant="outline" onClick={downloadTemplate} className="w-full gap-2">
                  <Download className="w-4 h-4" />
                  Download Template
                </Button>
                
                {uploadResult && (
                  <div className={`p-3 rounded-lg ${uploadResult.error ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                    {uploadResult.error ? (
                      <span className="text-red-700">{uploadResult.error}</span>
                    ) : (
                      <div className="text-green-700">
                        <p>Created: {uploadResult.created}, Updated: {uploadResult.updated}</p>
                        {uploadResult.errors?.length > 0 && (
                          <div className="text-red-600 text-sm mt-2">
                            {uploadResult.errors.slice(0, 3).map((err, i) => <p key={i}>{err}</p>)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Low Stock Filter */}
          {lowStockInfo && lowStockInfo.count > 0 && (
            <Button 
              variant={showLowStockOnly ? "default" : "outline"} 
              size="sm" 
              className={`gap-1.5 ${showLowStockOnly ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
              onClick={() => { setShowLowStockOnly(!showLowStockOnly); setShowHighStockOnly(false); }}
              data-testid="low-stock-filter-btn"
            >
              <AlertCircle className="w-3.5 h-3.5" />
              Low Stock ({lowStockInfo.count})
            </Button>
          )}

          {/* High Stock Filter */}
          {highStockCount > 0 && (
            <Button 
              variant={showHighStockOnly ? "default" : "outline"} 
              size="sm" 
              className={`gap-1.5 ${showHighStockOnly ? 'bg-green-600 hover:bg-green-700' : ''}`}
              onClick={() => { setShowHighStockOnly(!showHighStockOnly); setShowLowStockOnly(false); }}
              data-testid="high-stock-filter-btn"
            >
              <Package className="w-3.5 h-3.5" />
              High Stock ({highStockCount})
            </Button>
          )}

          {/* Add Product */}
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-1.5" onClick={() => setFormData(prev => ({ ...prev, sku: selectedSku }))}>
                <Plus className="w-3.5 h-3.5" />
                Add
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingProduct ? "Edit Product" : "Add New Product"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>SKU *</Label>
                    <Select
                      value={formData.sku}
                      onValueChange={(value) => setFormData({ ...formData, sku: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SKU_TYPES.map((sku) => (
                          <SelectItem key={sku.id} value={sku.id}>{sku.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Grade *</Label>
                    <Select
                      value={formData.grade}
                      onValueChange={(value) => setFormData({ ...formData, grade: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select grade" />
                      </SelectTrigger>
                      <SelectContent>
                        {GRADES.map((grade) => (
                          <SelectItem key={grade} value={grade}>{grade}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>KGs Pack *</Label>
                    <Input
                      type="number"
                      value={formData.kgs_pack}
                      onChange={(e) => setFormData({ ...formData, kgs_pack: e.target.value })}
                      placeholder="e.g., 10"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Quantity (Qty) *</Label>
                    <Input
                      type="number"
                      value={formData.qty}
                      onChange={(e) => setFormData({ ...formData, qty: e.target.value })}
                      placeholder="Stock quantity"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Base Price *</Label>
                    <Input
                      type="number"
                      value={formData.base_price}
                      onChange={(e) => setFormData({ ...formData, base_price: e.target.value })}
                      placeholder="₹/kg"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Min Price *</Label>
                    <Input
                      type="number"
                      value={formData.min_price}
                      onChange={(e) => setFormData({ ...formData, min_price: e.target.value })}
                      placeholder="₹/kg"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Max Price *</Label>
                    <Input
                      type="number"
                      value={formData.max_price}
                      onChange={(e) => setFormData({ ...formData, max_price: e.target.value })}
                      placeholder="₹/kg"
                      required
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit">{editingProduct ? "Update" : "Add"}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Quick Search - Dropdown Selection */}
      <div className="flex items-center gap-2 mb-3">
        <div className="flex items-center gap-1.5 text-xs">
          <Search className="w-3.5 h-3.5 text-muted-foreground" />
          <Select value={searchGrade} onValueChange={(val) => {
            setSearchGrade(val);
            // Auto-clear after 30 seconds
            if (val) {
              setTimeout(() => setSearchGrade(""), 30000);
            }
          }}>
            <SelectTrigger className="w-28 h-7 text-xs">
              <SelectValue placeholder="Grade" />
            </SelectTrigger>
            <SelectContent className="max-h-60">
              {uniqueGrades.map((grade) => (
                <SelectItem key={grade} value={grade} className="text-xs">{grade}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Select value={searchSku} onValueChange={setSearchSku}>
          <SelectTrigger className="w-28 h-7 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SKU_TYPES.map((sku) => (
              <SelectItem key={sku.id} value={sku.id} className="text-xs">{sku.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {searchGrade && (
          <div className="flex items-center gap-2 px-3 py-1 bg-primary/10 rounded border border-primary/20">
            {searchResult ? (
              <>
                <span className="text-xs font-medium">{searchResult.grade}</span>
                <span className="text-xs text-muted-foreground">|</span>
                <span className="text-xs"><b>{searchResult.qty > 0 ? searchResult.qty : 0}</b> qty</span>
                <span className="text-xs text-muted-foreground">|</span>
                <span className="text-xs text-primary font-semibold">
                  {searchResult.base_price > 0 ? `₹${searchResult.base_price}` : '-'}
                </span>
              </>
            ) : (
              <span className="text-xs text-red-500">Not in {searchSku}</span>
            )}
          </div>
        )}
      </div>

      {/* Main Tabs: Effective Stock | FG Stock | Ordered Grades | Dispatchable Orders */}
      <div className="flex items-center gap-1 mb-3 bg-muted rounded-lg p-0.5 w-fit" data-testid="product-tabs">
        <Button variant={activeProductTab === 'effective' ? 'default' : 'ghost'} size="sm" className="h-8 px-4 text-xs"
          onClick={() => setActiveProductTab('effective')} data-testid="tab-effective">
          Effective Stock
        </Button>
        <Button variant={activeProductTab === 'fg' ? 'default' : 'ghost'} size="sm" className="h-8 px-4 text-xs"
          onClick={() => setActiveProductTab('fg')} data-testid="tab-fg">
          FG Stock
        </Button>
        <Button variant={activeProductTab === 'ordered' ? 'default' : 'ghost'} size="sm" className="h-8 px-4 text-xs"
          onClick={() => setActiveProductTab('ordered')} data-testid="tab-ordered">
          Ordered Grades
        </Button>
        <Button variant={activeProductTab === 'dispatchable' ? 'default' : 'ghost'} size="sm" className="h-8 px-4 text-xs"
          onClick={() => { setActiveProductTab('dispatchable'); fetchDispatchableOrders(); }} data-testid="tab-dispatchable">
          Dispatchable Orders
        </Button>
      </div>

      {/* Effective Stock Tab - SKU button filtered */}
      {activeProductTab === 'effective' && (() => {
        const effBySku = effectiveStock.filter(r => r.sku === selectedSku && (r.fg_qty > 0 || r.ordered_qty > 0 || r.effective !== 0));
        const effSkuTotals = SKU_TYPES.reduce((acc, s) => {
          acc[s.id] = effectiveStock.filter(r => r.sku === s.id).reduce((sum, r) => sum + r.effective, 0);
          return acc;
        }, {});
        const totalOrderedQty = effBySku.reduce((sum, r) => sum + (r.ordered_qty || 0), 0);
        const totalFgQty = effBySku.reduce((sum, r) => sum + (r.fg_qty || 0), 0);
        const totalEffective = effBySku.reduce((sum, r) => sum + (r.effective || 0), 0);
        const sortedEff = sortByGradeOrder(effBySku, selectedSku);
        return (<>
          <div className="flex flex-wrap gap-2 mb-4">
            {SKU_TYPES.map((sku) => (
              <Button key={sku.id} variant={selectedSku === sku.id ? "default" : "outline"} size="sm"
                onClick={() => setSelectedSku(sku.id)} className={`gap-1.5 ${selectedSku === sku.id ? '' : sku.color}`}>
                {sku.label}
                <Badge variant="secondary" className="ml-1 text-xs">{Math.round(effSkuTotals[sku.id] || 0)}</Badge>
              </Button>
            ))}
          </div>
          <Card className="border-border/60 mb-4" data-testid="effective-stock-card">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table className="w-auto">
                  <TableHeader>
                    <TableRow className="bg-violet-100">
                      {isAdmin && <TableHead className="w-8 py-2 px-1"></TableHead>}
                      <TableHead className="font-bold py-2 px-3 text-xs text-violet-900">Grade</TableHead>
                      <TableHead className="font-bold py-2 px-3 text-xs text-right text-violet-900">FG Stock <span className="ml-1 text-violet-600 font-normal">({totalFgQty})</span></TableHead>
                      <TableHead className="font-bold py-2 px-3 text-xs text-right text-amber-700">Ordered <span className="ml-1 font-normal">({totalOrderedQty})</span></TableHead>
                      <TableHead className="font-bold py-2 px-3 text-xs text-right text-violet-900">Effective <span className="ml-1 text-violet-600 font-normal">({Math.round(totalEffective)})</span></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedEff.map((r, i) => (
                      <TableRow key={`${r.grade}-${r.sku}`}
                        draggable={isAdmin}
                        onDragStart={() => isAdmin && setGradeDragIdx(i)}
                        onDragOver={e => { if (isAdmin) e.preventDefault(); }}
                        onDrop={() => isAdmin && handleGradeDragEnd(sortedEff, i, selectedSku)}
                        className={`${r.effective < 0 ? 'bg-red-50' : ''} ${gradeDragIdx === i ? 'opacity-50' : ''} ${isAdmin ? 'cursor-grab active:cursor-grabbing' : ''}`}>
                        {isAdmin && <TableCell className="py-1 px-1 text-center"><GripVertical className="w-3.5 h-3.5 text-gray-300" /></TableCell>}
                        <TableCell className="py-1.5 px-3 text-xs font-semibold">{r.grade}</TableCell>
                        <TableCell className="py-1.5 px-3 text-xs text-right">{r.fg_qty}</TableCell>
                        <TableCell className="py-1.5 px-3 text-xs text-right">
                          {r.ordered_qty > 0 ? (
                            <button onClick={() => fetchOrderedGradeDetails(r.grade, r.sku)}
                              className="text-amber-700 font-semibold underline decoration-dotted hover:text-amber-900 cursor-pointer"
                              data-testid={`ordered-qty-${r.grade}-${r.sku}`}>{r.ordered_qty}</button>
                          ) : '-'}
                        </TableCell>
                        <TableCell className={`py-1.5 px-3 text-xs text-right font-bold ${r.effective < 0 ? 'text-red-600' : 'text-emerald-700'}`}>{r.effective}</TableCell>
                      </TableRow>
                    ))}
                    {effBySku.length === 0 && (
                      <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground text-sm">No stock in {SKU_TYPES.find(s => s.id === selectedSku)?.label}</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>);
      })()}

      {/* Ordered Grades Tab - SKU button filtered */}
      {activeProductTab === 'ordered' && (() => {
        const ordBySku = orderedGrades.filter(r => r.sku === selectedSku);
        const ordSkuTotals = SKU_TYPES.reduce((acc, s) => {
          acc[s.id] = orderedGrades.filter(r => r.sku === s.id).reduce((sum, r) => sum + r.qty, 0);
          return acc;
        }, {});
        return (<>
          <div className="flex flex-wrap gap-2 mb-4">
            {SKU_TYPES.map((sku) => (
              <Button key={sku.id} variant={selectedSku === sku.id ? "default" : "outline"} size="sm"
                onClick={() => setSelectedSku(sku.id)} className={`gap-1.5 ${selectedSku === sku.id ? '' : sku.color}`}>
                {sku.label}
                <Badge variant="secondary" className="ml-1 text-xs">{ordSkuTotals[sku.id] || 0}</Badge>
              </Button>
            ))}
          </div>
          <Card className="border-border/60 mb-4" data-testid="ordered-grades-card">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table className="w-auto">
                  <TableHeader>
                    <TableRow className="bg-amber-100">
                      <TableHead className="font-bold py-2 px-3 text-xs text-amber-900">Grade</TableHead>
                      <TableHead className="font-bold py-2 px-3 text-xs text-right text-amber-900">Qty</TableHead>
                      <TableHead className="font-bold py-2 px-3 text-xs text-right text-amber-900">Orders</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ordBySku.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell className="py-1.5 px-3 text-xs font-semibold">{r.grade}</TableCell>
                        <TableCell className="py-1.5 px-3 text-xs text-right font-bold">{r.qty}</TableCell>
                        <TableCell className="py-1.5 px-3 text-xs text-right text-muted-foreground">{r.orders}</TableCell>
                      </TableRow>
                    ))}
                    {ordBySku.length === 0 && (
                      <TableRow><TableCell colSpan={3} className="text-center py-8 text-muted-foreground text-sm">No ordered grades in {SKU_TYPES.find(s => s.id === selectedSku)?.label}</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>);
      })()}

      {/* Dispatchable Orders Tab */}
      {activeProductTab === 'dispatchable' && (
        <Card className="border-border/60 mb-4" data-testid="dispatchable-orders-card">
          <CardContent className="p-0">
            {dispatchableLoading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin" /></div>
            ) : dispatchableOrders.length > 0 ? (
              <div className="overflow-x-auto">
                {savingPriority && <div className="text-center text-xs text-muted-foreground py-1 bg-blue-50">Saving priority...</div>}
                <Table>
                  <TableHeader>
                    <TableRow className="bg-indigo-100">
                      <TableHead className="w-8 py-2 px-1"></TableHead>
                      <TableHead className="font-bold py-2 px-2 text-xs text-indigo-900">#</TableHead>
                      <TableHead className="font-bold py-2 px-2 text-xs text-indigo-900">Order#</TableHead>
                      <TableHead className="font-bold py-2 px-2 text-xs text-indigo-900">Last Drag</TableHead>
                      <TableHead className="font-bold py-2 px-2 text-xs text-indigo-900">Customer</TableHead>
                      <TableHead className="font-bold py-2 px-2 text-xs text-right text-indigo-900">Outstanding</TableHead>
                      <TableHead className="font-bold py-2 px-2 text-xs text-right text-indigo-900">Qty</TableHead>
                      <TableHead className="font-bold py-2 px-2 text-xs text-indigo-900">Payment</TableHead>
                      <TableHead className="font-bold py-2 px-2 text-xs text-indigo-900">Dispatch</TableHead>
                      <TableHead className="font-bold py-2 px-2 text-xs text-indigo-900">Short Grades</TableHead>
                      <TableHead className="font-bold py-2 px-2 text-xs text-indigo-900">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dispatchableOrders.map((o, idx) => {
                      const statusColor = o.dispatch_status === 'dispatchable' ? 'bg-green-50' : o.dispatch_status === 'partial' ? 'bg-amber-50' : o.dispatch_status === 'blocked' ? 'bg-red-50' : '';
                      const statusBadge = o.dispatch_status === 'dispatchable'
                        ? <Badge className="bg-green-100 text-green-800 text-[9px] px-1.5 py-0"><CheckCircle className="w-3 h-3 mr-0.5" />Ready</Badge>
                        : o.dispatch_status === 'partial'
                        ? <button onClick={() => openPartialDispatch(o)} className="cursor-pointer"><Badge className="bg-amber-100 text-amber-800 text-[9px] px-1.5 py-0 hover:bg-amber-200"><TriangleAlert className="w-3 h-3 mr-0.5" />Partial</Badge></button>
                        : o.dispatch_status === 'blocked'
                        ? <button onClick={() => openPartialDispatch(o)} className="cursor-pointer"><Badge className="bg-red-100 text-red-800 text-[9px] px-1.5 py-0 hover:bg-red-200"><AlertCircle className="w-3 h-3 mr-0.5" />Blocked</Badge></button>
                        : <Badge variant="outline" className="text-[9px] px-1.5 py-0">-</Badge>;
                      return (
                        <TableRow
                          key={o.id}
                          draggable
                          onDragStart={() => handleDragStart(idx)}
                          onDragOver={(e) => handleDragOver(e, idx)}
                          onDrop={(e) => handleDrop(e, idx)}
                          className={`${statusColor} ${dragIdx === idx ? 'opacity-50' : ''} cursor-grab active:cursor-grabbing transition-colors border-b`}
                          data-testid={`dispatchable-row-${idx}`}
                        >
                          <TableCell className="py-1.5 px-1 text-center">
                            <GripVertical className="w-3.5 h-3.5 text-muted-foreground/50" />
                          </TableCell>
                          <TableCell className="py-1.5 px-2 text-xs text-muted-foreground font-mono">{idx + 1}</TableCell>
                          <TableCell className="py-1.5 px-2 text-xs font-mono font-bold">{o.order_number}</TableCell>
                          <TableCell className="py-1.5 px-2 text-xs text-muted-foreground">
                            {o.last_dragged_by ? (
                              <span title={o.last_dragged_at ? new Date(o.last_dragged_at).toLocaleString() : ''}>
                                <User className="w-3 h-3 inline mr-0.5" />{o.last_dragged_by}
                              </span>
                            ) : '-'}
                          </TableCell>
                          <TableCell className="py-1.5 px-2 text-xs truncate" style={{maxWidth: '130px'}} title={o.customer_name}>{o.customer_name}</TableCell>
                          <TableCell className="py-1.5 px-2 text-xs text-right">
                            <button
                              onClick={() => { setLedgerPartyName(o.customer_name); setLedgerOpen(true); }}
                              className={`font-semibold underline decoration-dotted cursor-pointer ${o.outstanding > 0 ? 'text-red-600' : o.outstanding < 0 ? 'text-green-600' : 'text-muted-foreground'}`}
                              data-testid={`outstanding-${o.id}`}
                            >
                              {o.outstanding !== 0 ? `₹${Math.abs(Math.round(o.outstanding)).toLocaleString('en-IN')}` : '0'}
                              {o.outstanding < 0 ? ' CR' : ''}
                            </button>
                          </TableCell>
                          <TableCell className="py-1.5 px-2 text-xs text-right font-semibold">{o.total_qty}</TableCell>
                          <TableCell className="py-1.5 px-2 text-xs">{o.payment_terms || '-'}</TableCell>
                          <TableCell className="py-1.5 px-2">{statusBadge}</TableCell>
                          <TableCell className="py-1.5 px-2 text-xs">
                            {o.short_grades?.length > 0 ? (
                              <div className="space-y-0.5">
                                <span className="text-[9px] font-bold text-red-800 mr-1">Short: {o.short_total_qty}</span>
                                {o.short_grades.map((s, si) => (
                                  <span key={si} className="inline-block bg-red-100 text-red-700 text-[9px] px-1.5 py-0.5 rounded mr-1">
                                    {s.grade} {s.sku} <b>-{s.short}</b>
                                  </span>
                                ))}
                              </div>
                            ) : o.dispatch_status === 'dispatchable' ? (
                              <span className="text-green-600 text-[10px]">All covered</span>
                            ) : '-'}
                          </TableCell>
                          <TableCell className="py-1.5 px-2">
                            {o.status !== 'to_sort' && (
                              <Button size="sm" variant="outline" className="h-6 px-2 text-[10px] gap-1"
                                onClick={() => sendToSort(o.id, o.order_number)}
                                data-testid={`send-to-sort-${o.id}`}
                              >
                                <ArrowRight className="w-3 h-3" /> To Sort
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <div className="px-3 py-2 bg-muted/30 border-t text-[10px] text-muted-foreground flex gap-4">
                  <span>Total: <b>{dispatchableOrders.length}</b> orders</span>
                  <span className="text-green-700">Dispatchable: <b>{dispatchableOrders.filter(o => o.dispatch_status === 'dispatchable').length}</b></span>
                  <span className="text-amber-700">Partial: <b>{dispatchableOrders.filter(o => o.dispatch_status === 'partial').length}</b></span>
                  <span className="text-red-700">Blocked: <b>{dispatchableOrders.filter(o => o.dispatch_status === 'blocked').length}</b></span>
                  <span className="ml-auto text-muted-foreground/70">Drag rows to change dispatch priority (FIFO)</span>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <Package className="w-12 h-12 mb-3 text-muted-foreground/30" />
                <p className="text-sm font-medium text-muted-foreground">No pending orders</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}



      {/* FG Stock Tab - existing SKU buttons + products table */}
      {activeProductTab === 'fg' && (<>

      {/* SKU Buttons - Show Total Qty */}
      <div className="flex flex-wrap gap-2 mb-4">
        {SKU_TYPES.map((sku) => {
          const totalQty = getSkuTotalQty(sku.id);
          return (
            <Button
              key={sku.id}
              variant={selectedSku === sku.id ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedSku(sku.id)}
              className={`gap-1.5 ${selectedSku === sku.id ? '' : sku.color}`}
              data-testid={`sku-btn-${sku.id}`}
            >
              {sku.label}
              <Badge variant="secondary" className="ml-1 text-xs">
                {totalQty > 0 ? totalQty.toLocaleString() : '0'}
              </Badge>
            </Button>
          );
        })}
      </div>

      {/* Products Table - Compact */}
      <Card className="border-border/60">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : (() => { const sortedFgProducts = sortByGradeOrder(filteredProducts, selectedSku); return sortedFgProducts.length > 0 ? (
            <div className="overflow-x-auto max-h-[calc(100vh-220px)] overflow-y-auto">
              <Table className="w-auto">
                <TableHeader>
                  <TableRow className="bg-secondary/50">
                    {isAdmin && <TableHead className="w-8 py-2 px-1"></TableHead>}
                    <TableHead className="font-semibold py-2 px-3 text-xs">Grade</TableHead>
                    <TableHead className="font-semibold py-2 px-3 text-xs">Pack</TableHead>
                    <TableHead className="font-semibold py-2 px-3 text-xs text-right">Qty</TableHead>
                    <TableHead className="font-semibold py-2 px-3 text-xs text-right">Base ₹</TableHead>
                    <TableHead className="font-semibold py-2 px-3 text-xs text-right">Min ₹</TableHead>
                    <TableHead className="font-semibold py-2 px-3 text-xs text-right">Max ₹</TableHead>
                    <TableHead className="font-semibold py-2 px-2 text-xs w-14"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedFgProducts.map((product, i) => (
                    <TableRow key={product.id}
                      draggable={isAdmin}
                      onDragStart={() => isAdmin && setGradeDragIdx(i)}
                      onDragOver={e => { if (isAdmin) e.preventDefault(); }}
                      onDrop={() => isAdmin && handleGradeDragEnd(sortedFgProducts, i, selectedSku)}
                      className={`hover:bg-muted/50 ${gradeDragIdx === i ? 'opacity-50' : ''} ${isAdmin ? 'cursor-grab active:cursor-grabbing' : ''}`}
                      data-testid={`fg-stock-row-${i}`}>
                      {isAdmin && <TableCell className="py-1 px-1 text-center"><GripVertical className="w-3.5 h-3.5 text-gray-300" /></TableCell>}
                      <TableCell className="py-1.5 px-3 font-medium text-sm">
                        {product.grade}
                      </TableCell>
                      <TableCell className="py-1.5 px-3 text-xs">{product.kgs_pack} KG</TableCell>
                      <TableCell className="py-1.5 px-3 text-right text-xs font-semibold">
                        <span className="flex items-center justify-end gap-1">
                          {product.qty > 0 ? product.qty.toLocaleString() : '-'}
                          {isLowStock(product.id) && (
                            <Badge variant="outline" className="ml-1 px-1 py-0 text-[10px] border-amber-500 text-amber-600 bg-amber-50">
                              LOW
                            </Badge>
                          )}
                          {isHighStock(product) && !isLowStock(product.id) && (
                            <Badge variant="outline" className="ml-1 px-1 py-0 text-[10px] border-green-500 text-green-600 bg-green-50">
                              HIGH
                            </Badge>
                          )}
                        </span>
                      </TableCell>
                      <TableCell className="py-1.5 px-3 text-right text-xs font-medium text-primary">
                        {product.base_price > 0 ? formatCurrency(product.base_price) : '-'}
                      </TableCell>
                      <TableCell className="py-1.5 px-3 text-right text-xs">
                        {product.min_price > 0 ? formatCurrency(product.min_price) : '-'}
                      </TableCell>
                      <TableCell className="py-1.5 px-3 text-right text-xs">
                        {product.max_price > 0 ? formatCurrency(product.max_price) : '-'}
                      </TableCell>
                      <TableCell className="py-1.5 px-2">
                        <div className="flex justify-end gap-0.5">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => handleEdit(product)}
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-destructive hover:text-destructive"
                            onClick={() => handleDelete(product)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12">
              <Package className="w-12 h-12 mb-3 text-muted-foreground/30" />
              <p className="text-sm font-medium text-muted-foreground">No products in {SKU_TYPES.find(s => s.id === selectedSku)?.label}</p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                Upload Excel or add products
              </p>
            </div>
          ); })()}
        </CardContent>
      </Card>

      </>)}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Product</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {productToDelete?.grade} ({productToDelete?.kgs_pack} KG)?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Ordered Grade Details Dialog */}
      <Dialog open={orderedDetailOpen} onOpenChange={setOrderedDetailOpen}>
        <DialogContent className="sm:max-w-[650px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Package className="w-5 h-5 text-amber-600" />
              Standing Orders — {orderedDetailGrade?.grade} ({orderedDetailGrade?.sku})
            </DialogTitle>
            <DialogDescription>
              Orders pending/confirmed with this grade (not yet dispatched)
            </DialogDescription>
          </DialogHeader>
          {orderedDetailLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : orderedDetailData.length > 0 ? (
            <div className="mt-2">
              <div className="mb-3 flex gap-4 text-sm">
                <span className="text-muted-foreground">Total Qty: <b className="text-foreground">{orderedDetailData.reduce((s, r) => s + r.qty, 0)}</b></span>
                <span className="text-muted-foreground">Orders: <b className="text-foreground">{orderedDetailData.length}</b></span>
                <span className="text-muted-foreground">Value: <b className="text-emerald-700">₹{orderedDetailData.reduce((s, r) => s + r.amount, 0).toLocaleString('en-IN')}</b></span>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-amber-50">
                    <TableHead className="text-xs font-bold py-1.5">Order#</TableHead>
                    <TableHead className="text-xs font-bold py-1.5">Customer</TableHead>
                    <TableHead className="text-xs font-bold py-1.5">Date</TableHead>
                    <TableHead className="text-xs font-bold py-1.5 text-right">Qty</TableHead>
                    <TableHead className="text-xs font-bold py-1.5 text-right">Rate</TableHead>
                    <TableHead className="text-xs font-bold py-1.5 text-right">Amount</TableHead>
                    <TableHead className="text-xs font-bold py-1.5">Status</TableHead>
                    <TableHead className="text-xs font-bold py-1.5">Payment</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orderedDetailData.map((r, i) => (
                    <TableRow key={i} className="hover:bg-amber-50/50" data-testid={`ordered-detail-row-${i}`}>
                      <TableCell className="py-1.5 text-xs font-mono font-semibold">{r.order_number}</TableCell>
                      <TableCell className="py-1.5 text-xs truncate" style={{maxWidth: '140px'}} title={r.customer_name}>{r.customer_name}</TableCell>
                      <TableCell className="py-1.5 text-xs text-muted-foreground">{r.order_date ? r.order_date.slice(0, 10) : '-'}</TableCell>
                      <TableCell className="py-1.5 text-xs text-right font-bold">{r.qty}</TableCell>
                      <TableCell className="py-1.5 text-xs text-right">₹{r.rate?.toLocaleString('en-IN')}</TableCell>
                      <TableCell className="py-1.5 text-xs text-right font-semibold text-emerald-700">₹{r.amount?.toLocaleString('en-IN')}</TableCell>
                      <TableCell className="py-1.5">
                        <Badge variant="outline" className="text-[9px] px-1 py-0">{r.status?.toUpperCase()}</Badge>
                      </TableCell>
                      <TableCell className="py-1.5 text-xs text-muted-foreground">{r.payment_terms || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-center text-sm text-muted-foreground py-8">No standing orders found for this grade</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Ledger Dialog (from Outstanding click) */}
      <LedgerDialog open={ledgerOpen} onOpenChange={setLedgerOpen} initialParty={ledgerPartyName} />

      {/* Partial Dispatch Dialog */}
      <Dialog open={partialDialogOpen} onOpenChange={setPartialDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Package className="w-5 h-5 text-amber-600" />
              Partial Dispatch — {partialOrder?.order_number}
            </DialogTitle>
            <DialogDescription>
              Select grades and qty to dispatch. Remaining qty stays in the order.
            </DialogDescription>
          </DialogHeader>
          {partialOrder && (
            <div className="mt-2 space-y-3">
              <div className="text-sm text-muted-foreground">
                Customer: <b>{partialOrder.customer_name}</b> | Total Qty: <b>{partialOrder.total_qty}</b>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-amber-50">
                    <TableHead className="text-xs font-bold py-1.5">Grade</TableHead>
                    <TableHead className="text-xs font-bold py-1.5">SKU</TableHead>
                    <TableHead className="text-xs font-bold py-1.5 text-right">Order Qty</TableHead>
                    <TableHead className="text-xs font-bold py-1.5 text-right">Rate</TableHead>
                    <TableHead className="text-xs font-bold py-1.5 text-center">Dispatch Qty</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {partialItems.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="py-1.5 text-xs font-semibold">{item.grade}</TableCell>
                      <TableCell className="py-1.5 text-xs">{item.sku}</TableCell>
                      <TableCell className="py-1.5 text-xs text-right">{item.maxQty}</TableCell>
                      <TableCell className="py-1.5 text-xs text-right">₹{item.rate?.toLocaleString('en-IN')}</TableCell>
                      <TableCell className="py-1.5 text-center">
                        <Input
                          type="number"
                          min={0}
                          max={item.maxQty}
                          value={item.dispatchQty || ''}
                          onChange={(e) => {
                            const val = Math.min(parseInt(e.target.value) || 0, item.maxQty);
                            setPartialItems(prev => prev.map((p, i) => i === idx ? { ...p, dispatchQty: val } : p));
                          }}
                          className="h-7 w-20 text-xs text-center mx-auto"
                          data-testid={`partial-qty-${idx}`}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex items-center justify-between text-sm pt-2 border-t">
                <span className="text-muted-foreground">
                  Dispatching: <b>{partialItems.reduce((s, i) => s + (i.dispatchQty || 0), 0)}</b> | 
                  Remaining: <b>{partialItems.reduce((s, i) => s + (i.maxQty - (i.dispatchQty || 0)), 0)}</b>
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPartialDialogOpen(false)} disabled={sendingPartial}>Cancel</Button>
                  <Button size="sm" onClick={submitPartialToSort} disabled={sendingPartial} className="gap-1" data-testid="submit-partial-sort">
                    {sendingPartial ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight className="w-3 h-3" />}
                    Send to Sort
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
